import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSessionId, setLoading } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      console.log('AuthCallback: Processing hash:', hash);

      // NEW: unified Google OAuth — backend already created the session and
      // set the cookie; it just echoes the token in #session_token=... so
      // localStorage can mirror it for cross-domain Authorization-header
      // fallback. We don't call /auth/session in this branch.
      const tokenMatch = hash.match(/session_token=([^&]+)/);
      if (tokenMatch) {
        const tok = tokenMatch[1];
        console.log('AuthCallback: Found session_token (unified flow)');
        try {
          window.history.replaceState(null, '', window.location.pathname);
          localStorage.setItem('gs_session_token', tok);
          setLoading(false);
          navigate('/dashboard', { replace: true });
        } catch (err) {
          console.error('AuthCallback (unified) failed:', err);
          navigate('/login', { replace: true });
        }
        return;
      }

      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        console.log('AuthCallback: Found session_id, exchanging...');
        try {
          // Clear the hash immediately to prevent re-processing
          window.history.replaceState(null, '', window.location.pathname);
          
          const user = await exchangeSessionId(sessionId);
          console.log('AuthCallback: Exchange successful, user:', user?.email);
          
          // Verify token was stored
          const storedToken = localStorage.getItem('gs_session_token');
          console.log('AuthCallback: Token stored:', storedToken ? 'yes' : 'no');
          
          // Set loading to false before navigation to avoid flicker
          setLoading(false);
          // Navigate directly to dashboard
          navigate('/dashboard', { replace: true, state: { user, authenticated: true } });
        } catch (err) {
          console.error('Auth callback failed:', err);
          // Check if unauthorized domain - redirect to google.com
          if (err.message === 'UNAUTHORIZED_DOMAIN') {
            alert('Access restricted to @gsimaging.com.au email addresses only.');
            window.location.href = 'https://www.google.com';
            return;
          }
          navigate('/login', { replace: true });
        }
      } else {
        console.log('AuthCallback: No session_id in hash');
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location.hash, exchangeSessionId, navigate, setLoading]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-4 text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};
